import { useCallback, useEffect, useRef, useState } from 'react'
import { sassLine } from '../sass/engine.js'

export interface CameraCapabilities {
  torch: boolean
  zoom: { min: number; max: number; step: number } | null
  /** Can the lens be pointed at a spot the user chose? Android only, in practice. */
  focusPoint: boolean
}

export interface CameraState {
  /** `lost` is a stream that started and then ended under us. */
  status: 'idle' | 'starting' | 'live' | 'lost' | 'denied' | 'error'
  error: string | null
  capabilities: CameraCapabilities
  torchOn: boolean
}

/**
 * How soon after a restart a second ending counts as a loop rather than a
 * blip. Within this, the camera is handed back to the user with a button.
 */
const RESTART_FLOOR_MS = 4000

const INITIAL: CameraState = {
  status: 'idle',
  error: null,
  capabilities: { torch: false, zoom: null, focusPoint: false },
  torchOn: false,
}

/** The pieces of MediaTrackCapabilities no lib.dom version has caught up with. */
interface LensCapabilities {
  torch?: boolean
  zoom?: { min: number; max: number; step?: number }
  focusMode?: string[]
  pointsOfInterest?: unknown
}

/**
 * Camera lifecycle for the scanner.
 *
 * Installed iOS web apps do not persist camera permission between launches and
 * drop their tracks when backgrounded, so the stream is acquired per session
 * and torn down on hide rather than held open. Capabilities are re-read every
 * time: torch is Android-only in practice and zoom appeared only in Safari 17,
 * so neither can be assumed from a previous session.
 */
export function useCameraStream(active: boolean) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [state, setState] = useState<CameraState>(INITIAL)
  /** Whether the caller still wants a camera, readable from a track listener. */
  const wanted = useRef(false)
  const startRef = useRef<() => Promise<void>>(async () => undefined)
  const restartedAt = useRef(0)

  const stop = useCallback(() => {
    for (const track of streamRef.current?.getTracks() ?? []) track.stop()
    streamRef.current = null
    if (videoRef.current) videoRef.current.srcObject = null
    setState((s) => ({ ...INITIAL, status: s.status === 'denied' ? 'denied' : 'idle' }))
  }, [])

  const start = useCallback(async () => {
    if (streamRef.current) return
    setState((s) => ({ ...s, status: 'starting', error: null }))
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30, max: 30 },
        },
      })
      streamRef.current = stream

      const video = videoRef.current
      if (video) {
        video.srcObject = stream
        video.setAttribute('playsinline', '')
        video.muted = true
        await video.play().catch(() => undefined)
      }

      const track = stream.getVideoTracks()[0]
      const caps = (track?.getCapabilities?.() ?? {}) as MediaTrackCapabilities & LensCapabilities

      setState({
        status: 'live',
        error: null,
        torchOn: false,
        capabilities: {
          torch: caps.torch === true,
          zoom: caps.zoom
            ? { min: caps.zoom.min, max: caps.zoom.max, step: caps.zoom.step ?? 0.1 }
            : null,
          focusPoint: 'pointsOfInterest' in caps || (caps.focusMode ?? []).includes('single-shot'),
        },
      })

      /**
       * A capture that ends on its own.
       *
       * iOS ends the camera when something else on the phone takes the capture
       * hardware — including this app's own microphone, which the voice button
       * now asks for while the viewfinder is live. Nothing used to follow: the
       * hook went to `idle`, which draws no error and no button, so the screen
       * kept showing the last frame and quietly scanned nothing. Take it again.
       */
      track?.addEventListener('ended', () => {
        streamRef.current = null
        if (videoRef.current) videoRef.current.srcObject = null
        const now = Date.now()
        const looping = now - restartedAt.current < RESTART_FLOOR_MS
        restartedAt.current = now
        if (!wanted.current || looping) {
          setState({
            ...INITIAL,
            status: 'lost',
            error: sassLine('error_camera', {}) ?? 'Kameros vaizdas nutrūko.',
          })
          return
        }
        setState((s) => ({ ...s, status: 'starting', error: null }))
        void startRef.current()
      })
    } catch (err) {
      const denied =
        err instanceof DOMException &&
        (err.name === 'NotAllowedError' || err.name === 'SecurityError')
      setState({
        ...INITIAL,
        status: denied ? 'denied' : 'error',
        error: denied
          ? (sassLine('error_camera_denied', {}) ??
            'Kameros prieiga atmesta. Leiskite ją naršyklės nustatymuose.')
          : (sassLine('error_camera', {}) ?? 'Nepavyko paleisti kameros.'),
      })
    }
  }, [])

  // Reachable from a track listener that outlives the render it was added in.
  startRef.current = start

  const setTorch = useCallback(async (on: boolean) => {
    const track = streamRef.current?.getVideoTracks()[0]
    if (!track) return
    try {
      await track.applyConstraints({ advanced: [{ torch: on } as MediaTrackConstraintSet] })
      setState((s) => ({ ...s, torchOn: on }))
    } catch {
      setState((s) => ({ ...s, capabilities: { ...s.capabilities, torch: false } }))
    }
  }, [])

  /**
   * Point the lens at the spot the user tapped, in 0–1 frame coordinates.
   *
   * Android honours this; iOS Safari exposes neither `pointsOfInterest` nor a
   * settable `focusMode`, so there the constraint is skipped and only the ring
   * the caller draws happens. That is deliberate — a tap that does nothing at
   * all reads as a broken app, and a phone that cannot be told where to focus
   * still refocuses on its own once something in the frame moves.
   */
  const focusAt = useCallback(async (x: number, y: number) => {
    const track = streamRef.current?.getVideoTracks()[0]
    if (!track) return
    const caps = (track.getCapabilities?.() ?? {}) as MediaTrackCapabilities & LensCapabilities
    const point = { x: Math.min(1, Math.max(0, x)), y: Math.min(1, Math.max(0, y)) }
    const wanted: MediaTrackConstraintSet[] = []
    if ((caps.focusMode ?? []).includes('single-shot')) {
      wanted.push({
        focusMode: 'single-shot',
        pointsOfInterest: [point],
      } as MediaTrackConstraintSet)
    }
    // Some Android builds accept the point but refuse the mode in the same set.
    wanted.push({ pointsOfInterest: [point] } as MediaTrackConstraintSet)

    for (const advanced of wanted) {
      const applied = await track
        .applyConstraints({ advanced: [advanced] })
        .then(() => true)
        .catch(() => false)
      if (applied) return
    }
  }, [])

  const setZoom = useCallback(async (zoom: number) => {
    const track = streamRef.current?.getVideoTracks()[0]
    if (!track) return
    await track
      .applyConstraints({ advanced: [{ zoom } as MediaTrackConstraintSet] })
      .catch(() => undefined)
  }, [])

  useEffect(() => {
    wanted.current = active
    if (active) void start()
    else stop()
    return () => {
      wanted.current = false
      stop()
    }
  }, [active, start, stop])

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') stop()
      else if (active) void start()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [active, start, stop])

  return { videoRef, state, start, stop, setTorch, setZoom, focusAt }
}
